import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import {
  SectionHeading,
  Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import {
  Container as ContainerBase,
  ContentWithPaddingXl as ContentBase,
} from "components/misc/Layouts.js";
import { ReactComponent as CheckboxIcon } from "images/checkbox-circle.svg";

const Container = tw(ContainerBase)`bg-primary-100 text-primary-700 -mx-8 px-8`;
const ContentWithPaddingXl = tw(
  ContentBase
)`relative z-10 mx-auto px-0 py-10 sm:px-6 md:px-8 lg:px-12 xl:pr-24 sm:py-20 flex flex-col max-w-screen-xl`;
const HeaderContainer = tw.div`mt-10 w-full flex flex-col items-center`;
const Subheading = tw(SubheadingBase)`mb-4 text-gray-100`;
const Heading = tw(SectionHeading)`w-full text-white `;
const Description = tw(SectionDescription)`w-full text-gray-300 text-center`;

const CarouselContainer = styled.div`
  ${tw`relative w-full overflow-hidden`}
  scroll-behavior: smooth;
`;

const CarouselTrack = styled.div`
  ${tw`flex gap-6 transition-transform duration-300 ease-in-out`}
  transform: translateX(${props => props.translate}px);
`;

const CarouselButtons = tw.div`mt-6 flex justify-center gap-4`;

const CarouselButton = styled.button`
  ${tw`p-2 rounded-full bg-primary-500 text-white hover:bg-primary-700 focus:outline-none`}
`;

const PlansContainer = tw.div`mt-16`;
const Plan = styled.div`
  ${tw`w-full bg-white rounded-lg shadow-sm py-10 px-6 sm:px-10 lg:px-6 lg:py-10 xl:p-10 mx-auto flex flex-col justify-between shadow-raised`}
  min-width: 340px;
  max-width: 340px;
  width: 100%;
`;

const PlanHeader = styled.div`
  .nameAndFeaturedContainer {
    ${tw`flex flex-wrap flex-col sm:flex-row justify-between items-center`}
  }
  .name {
    ${tw`lg:text-lg xl:text-xl font-bold uppercase tracking-wider mr-3`}
  }
  .featuredText {
    ${tw`text-xs font-bold px-3 rounded py-2 uppercase bg-green-300 text-green-900 leading-none mt-4 sm:mt-0 w-full sm:w-auto text-center`}
  }
  .pricingContainer {
    ${tw`mt-6 flex items-end justify-between`}
    .currentPrice {
      ${tw`text-lg font-bold leading-none`}
      .bigText {
        ${tw`text-3xl font-bold`}
      }
    }
    .oldPrice {
      ${tw`text-gray-500 text-lg line-through hidden sm:block`}
    }
  }
  .description {
    ${tw`mt-8 font-medium text-gray-700 lg:text-sm xl:text-base`}
  }
`;
const PlanFeatures = styled.ul`
  ${tw`mt-10 flex-1 border-t lg:-mx-6 -mx-6 sm:-mx-10 py-10 px-6 sm:px-10 lg:p-6 xl:-mx-10 xl:p-10`}
  .feature {
    ${tw`flex items-start mt-6 first:mt-0`}
    .icon {
      ${tw`w-6 h-6 text-primary-500 flex-shrink-0`}
    }
    .text {
      ${tw`font-semibold text-primary-900 tracking-wide ml-3`}
    }
  }
`;

const PlanAction = tw.div`mt-4`;
const ActionButton = styled(PrimaryButtonBase)`
  ${tw`block text-center text-sm font-semibold tracking-wider w-full text-gray-100 bg-primary-100 px-6 py-4 rounded hover:bg-primary-700 focus:shadow-outline focus:outline-none transition-colors duration-300`}
`;

const WhiteBackgroundOverlay = tw.div`absolute inset-x-0 bottom-0 h-1/6 lg:h-1/3 bg-white z-0`;

export default ({
  subheading = "",
  heading = "Planos de Assinatura",
  description = "Escolha o plano ideal para sua prática profissional. Comece gratuitamente e escale conforme sua clínica cresce.",
  plans = null,
  primaryButtonText = "Comece Agora",
}) => {
  const defaultPlans = [
    {
      name: "Gratuito",
      price: ["R$ 0", ""],
      oldPrice: null,
      description: "Perfeito para começar e testar a plataforma",
      features: [
        "Até 5 pacientes",
        "Materiais didáticos básicos", 
        "Lembretes por email",
        "Suporte por email",
        "Acesso por 30 dias"
      ],
      url: "https://www.trataz.com.br/register",
    },
    {
      name: "Starter",
      price: ["R$ 97", "/mês"],
      oldPrice: "R$ 147",
      description: "Ideal para profissionais iniciantes",
      features: [
        "Até 30 pacientes",
        "Materiais didáticos completos",
        "Lembretes por WhatsApp e email",
        "Vídeos personalizados básicos",
        "Relatórios de progresso",
        "Suporte prioritário"
      ],
      url: "https://www.trataz.com.br/register",
      featured: "MAIS POPULAR"
    },
    {
      name: "Profissional",
      price: ["R$ 197", "/mês"],
      oldPrice: "R$ 297",
      description: "Para clínicas estabelecidas e profissionais experientes",
      features: [
        "Pacientes ilimitados",
        "Todos os recursos do Starter",
        "Vídeos personalizados avançados",
        "Integração com agenda",
        "Relatórios avançados e analytics",
        "Suporte 24/7 via WhatsApp",
        "Treinamento personalizado",
        "API para integrações"
      ],
      url: "https://www.trataz.com.br/register",
    },
  ];

  if (!plans) plans = defaultPlans;

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [translate, setTranslate] = React.useState(0);
  const trackRef = React.useRef(null);

  const handleNext = () => {
    if (currentIndex < plans.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setTranslate(prev => prev - (340 + 24)); // 340px (width) + 24px (gap)
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setTranslate(prev => prev + (340 + 24));
    }
  };

  return (
    <Container>
      <ContentWithPaddingXl>
        <HeaderContainer>
          {subheading && <Subheading>{subheading}</Subheading>}
          <Heading>{heading}</Heading>
          {description && <Description>{description}</Description>}
        </HeaderContainer>
        <PlansContainer>
          <CarouselContainer>
            <CarouselTrack ref={trackRef} translate={translate}>
              {plans.map((plan, index) => (
                <Plan key={index} featured={plan.featured}>
                  <PlanHeader>
                    <span className="nameAndFeaturedContainer">
                      <span className="name">{plan.name}</span>
                      {plan.featured && (
                        <span className="featuredText">{plan.featured}</span>
                      )}
                    </span>
                    <div className="pricingContainer">
                      <span className="currentPrice">
                        <span className="bigText">{plan.price[0]}</span>
                        {plan.price[1]}{" "}
                      </span>
                      {plan.oldPrice && (
                        <span className="oldPrice">{plan.oldPrice}</span>
                      )}
                    </div>
                    <p className="description">{plan.description}</p>
                  </PlanHeader>
                  <PlanFeatures>
                    {plan.features.map((feature, index) => (
                      <li className="feature" key={index}>
                        <CheckboxIcon className="icon" />
                        <span className="text">{feature}</span>
                      </li>
                    ))}
                  </PlanFeatures>
                  <PlanAction>
                    <ActionButton as="a" href={plan.url}>
                      {primaryButtonText}
                    </ActionButton>
                  </PlanAction>
                </Plan>
              ))}
            </CarouselTrack>
          </CarouselContainer>
          <CarouselButtons>
            <CarouselButton onClick={handlePrev} disabled={currentIndex === 0}>
              ←
            </CarouselButton>
            <CarouselButton 
              onClick={handleNext} 
              disabled={currentIndex === plans.length - 1}
            >
              →
            </CarouselButton>
          </CarouselButtons>
        </PlansContainer>
      </ContentWithPaddingXl>
      <WhiteBackgroundOverlay />
    </Container>
  );
};
